import { getSessid } from '$lib/stores/user';
import type { HttpResponse } from '$lib/types/HttpResponse';
import { isSafari } from '$lib/utils/functions';
import axios, { type AxiosResponse } from 'axios';

const CONTRACTS_PATH = '/api/contracts/';
const CONTRACTS_CACHE_PREFIX = 'spb-cache:/api/contracts/';
const CONTRACTS_CACHE_TTL_MS = 5 * 60 * 1000;

function isContractsUrl(url: string): boolean {
    return url.includes(CONTRACTS_PATH);
}

function hasSessionStorage(): boolean {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function buildCacheKey(url: string, params?: Record<string, unknown>): string {
    const sortedParams = params
        ? JSON.stringify(
              Object.keys(params)
                  .sort()
                  .reduce<Record<string, unknown>>((acc, key) => {
                      acc[key] = params[key];
                      return acc;
                  }, {}),
          )
        : '';
    return `${CONTRACTS_CACHE_PREFIX}${url}?${sortedParams}`;
}

function readContractsCache<T>(key: string): AxiosResponse<HttpResponse<T>> | null {
    if (!hasSessionStorage()) return null;
    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        const entry = JSON.parse(raw) as { ts: number; data: AxiosResponse<HttpResponse<T>> };
        if (Date.now() - entry.ts > CONTRACTS_CACHE_TTL_MS) {
            window.sessionStorage.removeItem(key);
            return null;
        }
        return entry.data;
    } catch {
        return null;
    }
}

function writeContractsCache<T>(key: string, data: AxiosResponse<HttpResponse<T>>): void {
    if (!hasSessionStorage()) return;
    try {
        const slim = { status: data.status, statusText: data.statusText, data: data.data };
        window.sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: slim }));
    } catch {
        // quota / serialization errors ignored
    }
}

export function invalidateContractsCache(): void {
    if (!hasSessionStorage()) return;
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(CONTRACTS_CACHE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => window.sessionStorage.removeItem(k));
}

/**
 * Service to manage HTTP requests
 */
export class HttpService {
    // protected endPointUrl: string = 'https://spb-backend.sinapto.tech';
    protected endPointUrl: string = import.meta.env.VITE_BACKEND_URL;

    constructor() {
        axios.defaults.withCredentials = true;
    }
    /**
     * Make a GET Request
     * @param url Url to make a GET request
     * @param options option of the request (queryParams, headers and so on)
     * @returns response obtained by Endpoint
     */
    protected async get<T>(url: string, options?: any) {
        options ??= {};
        options['headers'] ??= {};
        options['headers']['Content-Type'] = 'application/json';
        if (isSafari() && getSessid()) {
            options['headers']['X-sessid'] = getSessid()
        }

        const cacheable = isContractsUrl(url);
        const cacheKey = cacheable ? buildCacheKey(url, options.params) : '';
        if (cacheable) {
            const cached = readContractsCache<T>(cacheKey);
            if (cached) return cached as AxiosResponse<HttpResponse<T>>;
        }

        const response = await axios.get<HttpResponse<T>>(url, options);
        if (cacheable) writeContractsCache<T>(cacheKey, response);
        return response;
    }

    /**
     * Make a POST Request
     * @param url Url to make a POST request
     * @param data body data of the request
     * @returns response obtained by Endpoint
     */
    protected async post<T>(url: string, data?: any, options?: any) {
        options ??= {};
        options['headers'] ??= {};
        options['headers']['Content-Type'] = 'application/json';
        if (isSafari() && getSessid()) {
            options['headers']['X-sessid'] = getSessid()
        }

        try {
            const response = await axios.post<HttpResponse<T>>(url, data, options);
            invalidateContractsCache();
            return response;
        } catch (error) {
            return Promise.reject(error)
        }
    }

    /**
     * Make a DELETE Request
     * @param url Url to make a DELETE request
     * @param options body data of the request
     * @returns response obtained by Endpoint
     */
    protected async delete(url: string, data?: any) {
        data ??= {};
        data['headers'] ??= {};
        data['headers']['Content-Type'] = 'application/json';
        if (isSafari() && getSessid()) {
            data['headers']['X-sessid'] = getSessid()
        }

        const response = await axios.delete(url, data);
        invalidateContractsCache();
        return response;
    }

    /**
     * Make a PUT Request
     * @param url Url to make a PUT request
     * @param options body data of the request
     * @returns response obtained by Endpoint
     */
    protected async put(url: string, data?: any) {
        const response = await axios.put(url, data, {
            headers: { 'Content-Type': 'application/json' },
        });
        invalidateContractsCache();
        return response;
    }
}
